import { useMutation } from "@tanstack/react-query";
import {
  Check,
  ClipboardCopy,
  Eye,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  ServerCog,
  ShieldCheck,
  UserRoundPlus,
} from "lucide-react";
import { useEffect, useState } from "react";

import { MutationMessage } from "../account/components";
import { ThemeSwitcher } from "../theme";
import { ConfigurationForm } from "./ConfigurationForm";
import {
  ManagedConfiguration,
  SetupPhase,
  emptySecrets,
  initialConfiguration,
  setupApi,
} from "./configuration";

interface TotpEnrollment {
  id: string;
  secret: string;
  provisioningUri: string;
  algorithm: string;
  digits: number;
  periodSeconds: number;
  expiresAt: string;
}

interface RecoveryCodesResult {
  codes: string[];
  generation: number;
  generatedAt: string;
}

const steps: Array<{ phase: SetupPhase; label: string; icon: typeof ServerCog }> = [
  { phase: "CONFIGURATION", label: "基础设施", icon: ServerCog },
  { phase: "ADMINISTRATOR", label: "最高管理员", icon: UserRoundPlus },
  { phase: "MFA", label: "双重验证", icon: ShieldCheck },
  { phase: "ACTIVE", label: "完成", icon: Check },
];

export function SetupWizard({
  phase,
  restartError,
  onComplete,
}: {
  phase: SetupPhase;
  restartError: string | null;
  onComplete: () => void;
}) {
  const [token, setToken] = useState("");
  const [configuration, setConfiguration] = useState<ManagedConfiguration>(initialConfiguration);
  const [tested, setTested] = useState(false);
  const [waitingForRestart, setWaitingForRestart] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState<string | null>(null);
  const [administrator, setAdministrator] = useState({
    username: "",
    email: "",
    password: "",
    confirmation: "",
    displayName: "",
    locale: "zh-CN",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Taipei",
  });
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (phase !== "CONFIGURATION" || restartError) setWaitingForRestart(false);
  }, [phase, restartError]);

  const test = useMutation({
    mutationFn: () => setupApi<{ postgres: boolean; valkey: boolean }>("/api/v1/setup/connection-tests", token, {
      method: "POST",
      body: JSON.stringify(configuration),
    }),
    onSuccess: () => setTested(true),
  });
  const save = useMutation({
    mutationFn: () => setupApi<{ revision: string; generatedEmailWebhookSecret: string | null }>("/api/v1/setup/configuration", token, {
      method: "PUT",
      body: JSON.stringify(configuration),
    }),
    onSuccess: (result) => {
      setTested(true);
      setWaitingForRestart(true);
      setWebhookSecret(result.generatedEmailWebhookSecret);
    },
  });
  const createAdministrator = useMutation({
    mutationFn: () => {
      if (administrator.password !== administrator.confirmation) throw new Error("两次输入的密码不一致。");
      return setupApi("/api/v1/setup/administrator", token, {
        method: "POST",
        body: JSON.stringify({
          username: administrator.username,
          email: administrator.email,
          password: administrator.password,
          displayName: administrator.displayName,
          locale: administrator.locale,
          timeZone: administrator.timeZone,
        }),
      });
    },
    onSuccess: () => setAdministrator((value) => ({ ...value, password: "", confirmation: "" })),
  });
  const beginTotp = useMutation({
    mutationFn: () => setupApi<TotpEnrollment>("/api/v1/setup/mfa/totp/enrollment", token, { method: "POST" }),
    onSuccess: setEnrollment,
  });
  const confirmTotp = useMutation({
    mutationFn: () => setupApi<RecoveryCodesResult>("/api/v1/setup/mfa/totp/confirmation", token, {
      method: "POST",
      body: JSON.stringify({ enrollmentId: enrollment?.id, code: totpCode }),
    }),
    onSuccess: (result) => {
      setRecoveryCodes(result.codes);
      setTotpCode("");
      setCompleted(true);
    },
  });

  const effectivePhase: SetupPhase = completed ? "ACTIVE" : phase;
  const currentStep = steps.findIndex((step) => step.phase === effectivePhase);
  const tokenReady = token.trim().length >= 32;

  return <div className="setup-shell">
    <aside className="setup-rail">
      <div className="setup-brand-row"><a className="identity-mark inverse" href="/setup"><Eye /><span>EvilEyeAuth</span></a><ThemeSwitcher className="on-rail" /></div>
      <div className="setup-rail-copy"><span>FIRST RUN</span><strong>初始化你的身份平台</strong><p>连接现有 PostgreSQL 与 Redis / Valkey，创建最高管理员并强制启用双重验证。</p></div>
      <ol className="setup-progress">{steps.map(({ phase: stepPhase, label, icon: Icon }, index) => <li className={index < currentStep ? "done" : index === currentStep ? "current" : ""} key={stepPhase}><span><Icon /></span><div><small>步骤 {index + 1}</small><strong>{label}</strong></div></li>)}</ol>
    </aside>
    <main className="setup-main">
      <header className="setup-header">
        <div><span className="eyebrow">SECURE BOOTSTRAP</span><h1>{effectivePhase === "CONFIGURATION" ? "连接基础设施" : effectivePhase === "ADMINISTRATOR" ? "创建最高管理员" : effectivePhase === "MFA" ? "启用双重验证" : "初始化完成"}</h1></div>
        {!completed && <label className="setup-token"><span>一次性 Setup Token</span><div><LockKeyhole /><input required minLength={32} autoComplete="off" type="password" value={token} onChange={(event) => setToken(event.target.value)} placeholder="粘贴服务启动日志中的 Token" /></div></label>}
      </header>

      {effectivePhase === "CONFIGURATION" && <>
        <p className="setup-lead">应用只保存连接参数和加密凭据，不会安装或托管数据库进程。提交后服务会在当前进程内自动重启。</p>
        {waitingForRestart ? <RestartingState secret={webhookSecret} /> : <ConfigurationForm
          value={configuration}
          secrets={emptySecrets}
          busy={!tokenReady || test.isPending || save.isPending}
          error={test.error ?? save.error ?? (restartError ? new Error(restartError) : undefined)}
          tested={tested}
          submitLabel="保存并继续"
          onChange={(value) => { setConfiguration(value); setTested(false); }}
          onTest={() => test.mutate()}
          onSubmit={() => save.mutate()}
        />}
      </>}

      {effectivePhase === "ADMINISTRATOR" && <section className="setup-step-section">
        <p className="setup-lead">该账号会直接激活并获得最高管理员权限，无需邮件验证。完成后仍必须配置 TOTP。</p>
        {webhookSecret && <OneTimeSecret label="邮件事件签名密钥" value={webhookSecret} />}
        <form className="settings-form" onSubmit={(event) => { event.preventDefault(); createAdministrator.mutate(); }}>
          <div className="field-grid">
            <label><span>用户名</span><input required minLength={3} maxLength={32} autoComplete="username" value={administrator.username} onChange={(event) => setAdministrator({ ...administrator, username: event.target.value })} /></label>
            <label><span>显示名称</span><input required maxLength={80} value={administrator.displayName} onChange={(event) => setAdministrator({ ...administrator, displayName: event.target.value })} /></label>
            <label><span>邮箱</span><input required type="email" maxLength={254} autoComplete="email" value={administrator.email} onChange={(event) => setAdministrator({ ...administrator, email: event.target.value })} /></label>
            <label><span>时区</span><input required maxLength={64} value={administrator.timeZone} onChange={(event) => setAdministrator({ ...administrator, timeZone: event.target.value })} /></label>
            <label><span>密码</span><input required minLength={12} maxLength={128} type="password" autoComplete="new-password" value={administrator.password} onChange={(event) => setAdministrator({ ...administrator, password: event.target.value })} /></label>
            <label><span>确认密码</span><input required minLength={12} maxLength={128} type="password" autoComplete="new-password" value={administrator.confirmation} onChange={(event) => setAdministrator({ ...administrator, confirmation: event.target.value })} /></label>
          </div>
          <MutationMessage error={createAdministrator.error} />
          <div className="setup-form-actions"><button className="primary-button" disabled={!tokenReady || createAdministrator.isPending} type="submit"><UserRoundPlus />创建最高管理员</button></div>
        </form>
      </section>}

      {effectivePhase === "MFA" && <section className="setup-step-section">
        <p className="setup-lead">用任意兼容 TOTP 的验证器添加账号。恢复码只显示一次，离开本页后无法再次读取。</p>
        {!enrollment && <div className="setup-callout"><ShieldCheck /><div><strong>管理员账号已创建</strong><span>下一步生成验证器密钥，并用当前验证码确认。</span></div><button className="primary-button" disabled={!tokenReady || beginTotp.isPending} type="button" onClick={() => beginTotp.mutate()}><KeyRound />生成 TOTP 密钥</button></div>}
        {enrollment && <div className="setup-enrollment">
          <div><span>手动输入密钥</span><code>{enrollment.secret}</code><small>{enrollment.algorithm} / {enrollment.digits} 位 / {enrollment.periodSeconds} 秒</small></div>
          <a className="secondary-button" href={enrollment.provisioningUri}>在验证器中打开</a>
          <form onSubmit={(event) => { event.preventDefault(); confirmTotp.mutate(); }}><label><span>当前验证码</span><input required autoFocus inputMode="numeric" pattern="[0-9]{6,8}" autoComplete="one-time-code" value={totpCode} onChange={(event) => setTotpCode(event.target.value)} placeholder="000000" /></label><button className="primary-button" disabled={confirmTotp.isPending} type="submit"><Check />确认并完成</button></form>
        </div>}
        <MutationMessage error={beginTotp.error ?? confirmTotp.error} />
      </section>}

      {effectivePhase === "ACTIVE" && <section className="setup-complete">
        <span><ShieldCheck /></span><h2>身份平台已准备好</h2><p>保存下面的恢复码，然后进入登录页面。Setup Token 已立即失效。</p>
        <RecoveryCodes codes={recoveryCodes} />
        <button className="primary-button" type="button" onClick={onComplete}><Check />我已保存，进入登录</button>
      </section>}
    </main>
  </div>;
}

function RestartingState({ secret }: { secret: string | null }) {
  return <section className="setup-restarting" role="status"><LoaderCircle className="spin" /><div><strong>正在应用配置并重启</strong><p>页面会自动恢复并进入管理员创建步骤。</p></div>{secret && <OneTimeSecret label="邮件事件签名密钥" value={secret} />}</section>;
}

function OneTimeSecret({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return <div className="setup-one-time"><div><strong>{label}</strong><span>只显示一次，请立即保存。</span></div><code>{value}</code><button className="secondary-button" type="button" onClick={() => { void navigator.clipboard.writeText(value); setCopied(true); }}><ClipboardCopy />{copied ? "已复制" : "复制"}</button></div>;
}

function RecoveryCodes({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false);
  return <div className="setup-recovery"><div className="code-grid">{codes.map((code) => <code key={code}>{code}</code>)}</div><button className="secondary-button" type="button" onClick={() => { void navigator.clipboard.writeText(codes.join("\n")); setCopied(true); }}><ClipboardCopy />{copied ? "已复制" : "复制全部恢复码"}</button></div>;
}
